import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { unitMapping, type Unit } from '#src/lib/style.ts';
import type { Color } from '#src/style/types.ts';

type Props = {
  /** URL of a single-color (white, transparent background) asset. */
  url: string;
  /** Fill color applied to the masked shape. Defaults to the ambient text color. */
  color?: Color;
  /** Height as a design unit (mirrors the old SetIcon `size` prop). */
  size?: Unit;
  /** Explicit height in px (mirrors the old SetLogoImg `height` prop). */
  height?: number;
  /** Explicit width in px (mirrors the old SetLogoImg `width` prop). */
  width?: number;
  /** Accessible label. When omitted the icon is treated as decorative. */
  title?: string;
  className?: string;
};

const Root = styled.span`
  position: relative;
  display: inline-flex;

  /*
   * The <img> is never painted (opacity: 0) but stays in layout, so the box
   * inherits the asset's intrinsic size exactly like a plain <img> would.
   * Setting only one dimension lets the other follow the intrinsic aspect ratio.
   */
  & > img {
    display: block;
    opacity: 0;
    pointer-events: none;
  }

  /*
   * The masked layer fills the box the <img> created. Because the box already
   * carries the asset's aspect ratio, mask-size: contain fills it with no
   * distortion. background-color is the recolor knob.
   */
  & > span {
    position: absolute;
    inset: 0;
    background-color: var(--mask-icon-color, currentColor);
    mask: var(--mask-icon-url) center / contain no-repeat;
  }
`;

export const MaskIcon = ({
  url,
  color,
  size,
  height,
  width,
  title,
  className,
}: Props) => {
  const imgStyle: CSSProperties = {
    height:
      size !== undefined
        ? unitMapping[size]
        : height !== undefined
          ? height
          : undefined,
    width: width !== undefined ? width : undefined,
  };

  return (
    <Root
      className={['mask-icon', className].filter(Boolean).join(' ')}
      style={
        {
          '--mask-icon-url': `url("${url}")`,
          '--mask-icon-color': color,
        } as CSSProperties
      }
    >
      <img
        src={url}
        alt={title ?? ''}
        aria-hidden={title ? undefined : true}
        style={imgStyle}
      />
      <span aria-hidden />
    </Root>
  );
};
